import { useRef, useEffect, useState } from 'react'
import { styled } from 'styled-components'

const Wrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`

const ButtonWrapper = styled.div`
    margin-top: 30px;
    width: 80%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
`

const ActionButtonGroup = styled.div`
    display: flex;
    gap: 15px;
`

const OutlineButton = styled.button`
    width: fit-content;
    padding: 10px 24px;
    background-color: #ffffff;
    color: #333333;
    border: 2px solid #e5e6eb;
    border-radius: 50px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        border-color: #1fa474;
        color: #1fa474;
    }
`

const VideoWrapper = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`

const VideoPlaceholder = styled.video`
    width: 800px;
    height: 500px;
    background-color: #e7effe;
    color: white;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
`

const RecordButton = styled.button`
    width: fit-content;
    padding: 12px 32px;
    background-color: #1fa474;
    color: #ffffff;
    border: none;
    border-radius: 50px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
        background-color: #188c5f;
    }
`

const StopButton = styled.button`
    all: unset;
    cursor: pointer;
    width: 200px;
    background-color: #ed6a5e;
    color: white;
    padding: 10px;
    display: flex;
    justify-content: center;
    border-radius: 8px;
    &:hover {
        cursor: pointer;
        background-color: #ed6a5e90;
    }
`
const SubmitButton = styled.button`
    width: fit-content;
    padding: 10px 24px;
    background-color: #ffffff;
    color: #333333;
    border: 2px solid #e5e6eb;
    border-radius: 50px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        border-color: #1fa474;
        color: #1fa474;
    }
`

const FlipButton = styled.button`
    width: fit-content;
    padding: 10px 24px;
    background-color: #ffffff;
    color: #333333;
    border: 2px solid #e5e6eb;
    border-radius: 50px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        border-color: #1fa474;
        color: #1fa474;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`

function getSupportedMimeType(): string {
    if (typeof MediaRecorder === 'undefined') return ''
    const candidates = ['video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm']
    return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? ''
}

interface Props {
    handleSubmit: (mediaBlobUrl: string | null, selectedSkill?: string) => void
    assessmentStatus:
        | 'start'
        | 'confirm'
        | 'uploading'
        | 'completed'
        | 'error'
        | 'default'
    selectedSkill?: string
    onPreviousSkill?: () => void
    onNextSkill?: () => void
}

export default function VideoRecorder(props: Props) {
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user')

    return (
        <VideoRecorderInner
            key={facingMode}
            {...props}
            facingMode={facingMode}
            onFlipCamera={() =>
                setFacingMode((m) => (m === 'user' ? 'environment' : 'user'))
            }
        />
    )
}

interface InnerProps extends Props {
    facingMode: 'user' | 'environment'
    onFlipCamera: () => void
}

function VideoRecorderInner({
    handleSubmit,
    assessmentStatus,
    selectedSkill,
    onPreviousSkill,
    onNextSkill,
    facingMode,
    onFlipCamera,
}: InnerProps) {
    const [recStatus, setRecStatus] = useState<
        'idle' | 'recording' | 'stopped'
    >('idle')
    const [mediaBlobUrl, setMediaBlobUrl] = useState<string | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const chunksRef = useRef<Blob[]>([])
    const liveVideoRef = useRef<HTMLVideoElement>(null)
    const pendingSubmitRef = useRef(false)
    const mimeType = getSupportedMimeType()

    // Acquire a single stream used for both preview and recording
    useEffect(() => {
        let cancelled = false
        navigator.mediaDevices
            .getUserMedia({ video: { facingMode }, audio: true })
            .then((stream) => {
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop())
                    return
                }
                streamRef.current = stream
                if (liveVideoRef.current) {
                    liveVideoRef.current.srcObject = stream
                }
            })
            .catch(() => {})
        return () => {
            cancelled = true
            streamRef.current?.getTracks().forEach((t) => t.stop())
            streamRef.current = null
        }
    }, [facingMode])

    // Attach stream to video element whenever the live video ref mounts
    useEffect(() => {
        if (liveVideoRef.current && streamRef.current) {
            liveVideoRef.current.srcObject = streamRef.current
        }
    })

    // Reset after completed upload
    useEffect(() => {
        if (assessmentStatus === 'completed') {
            if (mediaBlobUrl) URL.revokeObjectURL(mediaBlobUrl)
            setMediaBlobUrl(null)
            setRecStatus('idle')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assessmentStatus])

    function handleStartRecording() {
        if (!streamRef.current) return
        chunksRef.current = []
        const options: MediaRecorderOptions = mimeType ? { mimeType } : {}
        const recorder = new MediaRecorder(streamRef.current, options)
        mediaRecorderRef.current = recorder

        recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) chunksRef.current.push(e.data)
        }
        recorder.onstop = () => {
            const blob = new Blob(chunksRef.current, {
                type: mimeType || 'video/mp4',
            })
            const url = URL.createObjectURL(blob)
            setMediaBlobUrl(url)
            setRecStatus('stopped')
            if (pendingSubmitRef.current) {
                pendingSubmitRef.current = false
                handleSubmit(url, selectedSkill)
            }
        }

        // timeslice of 100 ms is required on iOS to flush data chunks reliably
        recorder.start(100)
        setRecStatus('recording')
    }

    function handleSubmitButtonClick() {
        pendingSubmitRef.current = true
        mediaRecorderRef.current?.stop()
    }

    return (
        <Wrapper>
            <VideoWrapper>
                {/* Live preview shown during idle and recording */}
                <VideoPlaceholder
                    ref={liveVideoRef}
                    autoPlay
                    muted
                    playsInline
                    style={{
                        display: recStatus === 'stopped' ? 'none' : 'block',
                    }}
                />
                {/* Playback shown after recording stops */}
                {recStatus === 'stopped' && mediaBlobUrl && (
                    <VideoPlaceholder src={mediaBlobUrl} controls playsInline />
                )}
            </VideoWrapper>
            {recStatus === 'idle' && 'Previewing'}
            {recStatus === 'recording' && 'Recording'}
            {recStatus === 'stopped' && 'Stopped'}
            <ButtonWrapper>
                <div style={{ display: 'flex', gap: '20px' }}>
                    <RecordButton
                        onClick={handleStartRecording}
                        disabled={recStatus === 'recording'}
                    >
                        Start {selectedSkill}
                    </RecordButton>
                    <SubmitButton
                        onClick={handleSubmitButtonClick}
                        disabled={recStatus !== 'recording'}
                    >
                        Stop & Submit
                    </SubmitButton>
                    <FlipButton
                        onClick={onFlipCamera}
                        disabled={recStatus === 'recording'}
                        title="Flip camera"
                    >
                        ⟳ Flip Camera
                    </FlipButton>
                </div>
                <ActionButtonGroup>
                    <OutlineButton onClick={onPreviousSkill}>
                        Last Skill
                    </OutlineButton>
                    <OutlineButton onClick={onNextSkill}>
                        Next Skill
                    </OutlineButton>
                </ActionButtonGroup>
            </ButtonWrapper>
        </Wrapper>
    )
}
